import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Lock, PackageSearch, Plus, Settings as SettingsIcon } from 'lucide-react';
import Button from '../components/Button';
import { expiryText } from '../components/shipments/gateMessages';
import { useGuard } from '../components/shipments/guardContext';
import AddShipmentDialog from '../components/shipments/AddShipmentDialog';
import ListTabs from '../components/shipments/ListTabs';
import NotificationBell from '../components/shipments/NotificationBell';
import Pager from '../components/shipments/Pager';
import ReviewQueue from '../components/shipments/ReviewQueue';
import ShipmentCards from '../components/shipments/ShipmentCards';
import ShipmentFilters from '../components/shipments/ShipmentFilters';
import ShipmentTable from '../components/shipments/ShipmentTable';
import { HealthStrip, SummaryChips } from '../components/shipments/SummaryChips';
import { Banner, EmptyState, SkeletonList } from '../components/shipments/Ui';
import { activeFilterCount, filtersToQuery, useShipmentFilters } from '../components/shipments/useShipmentFilters';
import { useMediaQuery } from '../components/shipments/useMediaQuery';
import { useNoticeFromState } from '../components/shipments/useNoticeFromState';
import { useRefreshOnReturn } from '../components/shipments/useRefreshOnReturn';
import { useRemote } from '../components/shipments/useRemote';
import { useToasts } from '../components/shipments/useToasts';
import { PAGE_SIZE, TABPANEL_ID } from '../constants/shipments';
import { shipmentsApi } from '../utils/shipmentsApi';

const DEFAULT_ORDER = { lastEvent: 'desc', created: 'desc', expected: 'asc', status: 'asc', carrier: 'asc' };
const EMPTY_TEXT = {
  active: 'Žádná aktivní zásilka.', review: 'Ke kontrole nic nečeká.', delivered: 'Zatím žádná doručená zásilka.', all: 'Zatím žádná zásilka.',
  deleted: 'Žádná smazaná zásilka.',
};

// Module home of Zásilky: summary, tabs, filters, list (table on desktop, cards on a phone) and the review queue.
const Shipments = () => {
  const navigate = useNavigate();
  const { status, ping, lock, locking, lockError } = useGuard();
  const { filters, setFilters, clearFilters } = useShipmentFilters();
  const { notify, host } = useToasts();
  useNoticeFromState(notify);
  const compact = useMediaQuery('(max-width: 759px)');
  const [showAdd, setShowAdd] = useState(false);
  const topRef = useRef(null);

  const summary = useRemote('summary', () => shipmentsApi.summary());
  const carriers = useRemote('carriers', () => shipmentsApi.carriers());
  const isReview = filters.tab === 'review';
  const listKey = isReview ? null : JSON.stringify(filtersToQuery(filters));
  const list = useRemote(listKey, (key) => shipmentsApi.list(JSON.parse(key)), filters.tab);

  useRefreshOnReturn(() => { summary.reload(); if (!isReview) list.reload(); });

  const rows = list.data?.items || [];
  const total = list.data?.total || 0;
  const sort = filters.sort || 'lastEvent';
  const order = filters.order || DEFAULT_ORDER[sort];
  const filterCount = activeFilterCount(filters);

  const onSort = (field) => {
    if (field === sort) { setFilters({ sort: field === 'lastEvent' ? '' : field, order: order === 'asc' ? 'desc' : 'asc' }); return; }
    setFilters({ sort: field === 'lastEvent' ? '' : field, order: DEFAULT_ORDER[field] === 'desc' ? '' : DEFAULT_ORDER[field] });
  };

  const onPage = (page) => {
    setFilters({ page });
    topRef.current?.scrollIntoView({ block: 'start' });
  };

  const expiry = expiryText(status);
  const reviewCount = summary.data?.reviewCount;

  return (
    <div className="shp-page">
      {host}
      <header className="shp-head">
        <h1 className="shp-title"><PackageSearch color="#E53935" aria-hidden="true" /> Zásilky</h1>
        <div className="shp-head__actions">
          <NotificationBell unread={summary.data?.unreadNotifications} onChanged={summary.reload} />
          <Link to="/zasilky/nastaveni" className="shp-btn" aria-label="Nastavení zásilek"><SettingsIcon size={18} aria-hidden="true" /> Nastavení</Link>
          <Button icon={Plus} onClick={() => setShowAdd(true)}>Přidat zásilku</Button>
          <Button variant="secondary" icon={Lock} loading={locking} onClick={lock}>Zamknout modul</Button>
        </div>
      </header>

      {lockError && <Banner tone="bad">{lockError}</Banner>}
      {summary.error && !summary.data && (
        <Banner tone="bad" action={<button type="button" className="shp-textbtn" onClick={summary.reload}>Zkusit znovu</button>}>Souhrn se nepodařilo načíst. {summary.error.message}</Banner>
      )}

      <SummaryChips summary={summary.data} filters={filters} onPick={(chip) => setFilters(chip.filters)} />
      <HealthStrip summary={summary.data} />

      <div ref={topRef} style={{ scrollMarginTop: 80 }}>
        <ListTabs value={filters.tab} onChange={(tab) => setFilters({ tab })} reviewCount={reviewCount} />
      </div>

      <div role="tabpanel" id={TABPANEL_ID} aria-labelledby={`shp-tab-${filters.tab}`}>
        {isReview ? (
          <ReviewQueue notify={notify} onChanged={summary.reload} />
        ) : (
          <>
            <ShipmentFilters filters={filters} setFilters={setFilters} clearFilters={clearFilters} carriers={carriers.data} />
            {list.error && (
              <Banner tone="bad" action={<button type="button" className="shp-textbtn" onClick={list.reload}>Zkusit znovu</button>}>
                Zásilky se nepodařilo načíst. {list.error.message}
              </Banner>
            )}
            {!list.loaded && !list.error && <SkeletonList />}
            {list.loaded && !list.error && rows.length === 0 && (
              <EmptyState
                title={filterCount > 0 ? 'Žádná zásilka neodpovídá filtrům' : EMPTY_TEXT[filters.tab]}
                action={filterCount > 0 ? <Button variant="secondary" onClick={clearFilters}>Vymazat filtry</Button> : undefined}
              >
                {filterCount > 0 ? 'Zkuste filtry upravit nebo je vymažte.'
                  : filters.tab === 'deleted' ? 'Smazané zásilky se tu zobrazí a půjdou obnovit.' : 'Nové zásilky se objeví po synchronizaci pošty, nebo je můžete přidat ručně.'}
              </EmptyState>
            )}
            {rows.length > 0 && (
              <div className={list.loading ? 'shp-dim' : undefined} aria-busy={list.loading}>
                <p className="shp-sr" role="status">Nalezeno zásilek: {total}</p>
                {compact ? <ShipmentCards rows={rows} /> : <ShipmentTable rows={rows} sort={sort} order={order} onSort={onSort} />}
              </div>
            )}
            <Pager total={total} page={filters.page} pageSize={PAGE_SIZE} onPage={onPage} />
          </>
        )}
      </div>

      <div className="shp-health" style={{ margin: '20px 4px 0' }}>
        {ping === 'ok' && <span><CheckCircle2 size={14} color="#4CAF50" aria-hidden="true" /> Zabezpečené spojení s modulem funguje.</span>}
        {ping === 'failed' && <span role="status" style={{ color: '#E65100' }}><AlertTriangle size={14} aria-hidden="true" /> Spojení s modulem se nepodařilo ověřit.</span>}
        {expiry && <span>{expiry}</span>}
      </div>

      {showAdd && (
        <AddShipmentDialog
          onClose={() => setShowAdd(false)}
          onCreated={(detail) => { setShowAdd(false); navigate(`/zasilky/${Number(detail?.shipment?.id)}`, { state: { notice: 'Zásilka byla přidána.' } }); }}
        />
      )}
    </div>
  );
};

export default Shipments;
